import { throwContextError } from "@/common/utils/core";
import { invoke, isObjectValue, isStringValue } from "@/common/utils/helpers";
import DeleteACD from "@equitable/brand-icons/DeleteACD";
import { EqActionButtonProps, EqDialog, EqTypography } from "@equitable/equx";
import { createContext, ReactNode, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

type DialogType = "custom" | "formReset" | "confirm" | "remove" | "removeInsured" | "removeRider" | "unsavedChanges" | "removeWithoutCancel";

type DialogProps = {
  title?: string;
  icon?: ReactNode | "delete";
  message: string | ReactNode;
  actionButtonLabel: string;
  actionButtonColor: EqActionButtonProps["color"];
  onAction?: () => void;
  cancelButtonLabel?: string;
  onCancel?: () => void;
  showCrossButton?: boolean;
  actionButtons: EqActionButtonProps[] | null;
}

type DialogContextType = {
  showDialog: (type: DialogType, props?: Partial<DialogProps>) => void;
  hideDialog: () => void;
  dialogProps: DialogProps | null;
  isOpen: boolean;
}

const DialogContext = createContext<DialogContextType>(null as unknown as DialogContextType);

export const DialogProvider: React.FC<{children: ReactNode}> = ({ children }) => {

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dialogProps, setDialogProps] = useState<DialogProps | null>(null);
  const pleaseProvideContent = "Please provide content...";

  const showDialog = (type: DialogType, props?: Partial<DialogProps>) => {
    if (isOpen) {
      return;
    }

    setDialogProps({ ...dialogs[type], ...props } as DialogProps);
    setIsOpen(true);
  };

  const hideDialog = () => {
    setDialogProps(null);
    setIsOpen(false);
  };

  const onCloseDialog = () => {
    if (isStringValue(dialogProps?.cancelButtonLabel)) {
      invoke(dialogProps?.onCancel);
    }

    hideDialog();
  };

  const genericRemove: Partial<DialogProps> = {
    title: t("areYouSure"),
    actionButtonLabel: t("remove"),
    actionButtonColor: "surfaceErrorDefault",
    cancelButtonLabel: t("cancel"),
    icon: "delete",
    showCrossButton: true
  };

  const genericRemoveWithoutCancel: Partial<DialogProps> = {
    title: t("areYouSure"),
    actionButtonLabel: t("remove"),
    actionButtonColor: "surfaceErrorDefault",
    icon: "delete",
    showCrossButton: false
  };

  const dialogs: Record<DialogType, Partial<DialogProps>> = {
    "custom": {
    },
    "confirm": {
      message: pleaseProvideContent,
      actionButtonLabel: t("confirm")
    },
    "formReset": {
      ...genericRemove,
      message: t("formResetAlertMessage"),
      actionButtonLabel: t("reset")
    },
    "remove": {
      ...genericRemove,
      message: pleaseProvideContent
    },
    "removeInsured": {
      ...genericRemove,
      message: t("insuredAlertMessage")
    },
    "removeRider": {
      ...genericRemove,
      message: t("riderAlertMessage")
    },
    "unsavedChanges": {
      ...genericRemove,
      title: t("unsavedChanges"),
      message: t("byExitingYouWillLoseAnyUnsavedChanged"),
      actionButtonLabel: t("exit"),
      cancelButtonLabel: t("stay")
    },
    "removeWithoutCancel": {
      ...genericRemoveWithoutCancel,
      message: pleaseProvideContent
    }
  };

  const getDialog = () => {

    if (!isOpen || !isObjectValue(dialogProps)) {
      return null;
    }

    const buttons: EqActionButtonProps[] = dialogProps?.actionButtons ?? [{
      onClick: () => {
        invoke(dialogProps?.onAction);
        hideDialog();
      },
      label: dialogProps?.actionButtonLabel,
      color: dialogProps?.actionButtonColor || "surfacePrimaryDark"
    }];

    if (isStringValue(dialogProps?.cancelButtonLabel)) {
      buttons.unshift({
        onClick: () => {
          invoke(dialogProps?.onCancel);
          hideDialog();
        },
        label: dialogProps?.cancelButtonLabel ?? t("cancel"),
        color: "surfacePrimaryDark",
        variant: "outlined"
      });
    }

    return (
      <EqDialog
        open={isOpen}
        title={dialogProps?.title || undefined}
        titleChildren={dialogProps?.icon === "delete" ? <DeleteACD /> : dialogProps?.icon}
        actionButtons={buttons}
        dividers={false}
        onClose={onCloseDialog}
        rightButtonIcon={dialogProps?.showCrossButton ? "X" : undefined}
        dialogActionsHorizontal
        fullWidth
        backdropClose={true}
      >
        <EqTypography variant="body3">
          {dialogProps?.message}
        </EqTypography>
      </EqDialog>
    );

  };

  const obj: DialogContextType = {
    showDialog,
    hideDialog,
    dialogProps,
    isOpen
  };

  return (
    <DialogContext.Provider value={obj}>
      {getDialog()}
      {children}
    </DialogContext.Provider>
  );
};

export const useDialog = () => useContext(DialogContext) ?? throwContextError("useDialog");
